import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { getCalendarRangeBasedOnFilterType } from "./schedule-helper-functions";
import { ScheduleMachineContext } from "./schedule.machine.types";
import { SportEventDataFieldNames } from "./datafields";
import { FilterType } from "./filter-rules";

/** Check start and end time of the event block compared to what is being shown */
export const isEventBlockValidByDateFilter = (startDate: Date, endDate: Date, eventBlock: EventBlock) =>
    startDate.getTime() <= eventBlock.startAt.getTime() && endDate.getTime() >= eventBlock.endAt.getTime();

/** Check if the venue of the event block is currently being shown */
export const isVenueSelected = (selectedVenues: Hierarchy<VenueLocation>[], eventBlock: EventBlock) => {
    const selectedVenueIds = selectedVenues.map(({ item }) => item.id);
    return selectedVenueIds.includes(eventBlock.venueLocation.id);
};

/** Check if the event blocks discipline is being shown */
export const isDisciplineSelected = (
    scheduleVersionHierarchy: Hierarchy<ScheduleVersion>,
    selectedDataFieldListItems: DataFieldListItem[],
    filterableDataFields: DataField[],
    eventBlock: EventBlock
) => {
    const { scheduleVersionId } = eventBlock;
    // Get the event block's schedule discipline as that has the discipline code datafield
    const eventBlockScheduleVersion = scheduleVersionHierarchy.children.find(({ item }) => item.id === scheduleVersionId);
    // Get the discipline datafield
    const disciplineDataField = filterableDataFields.find((dataField) => dataField.name === SportEventDataFieldNames.DISCIPLINE);
    // Get all selected discipline Ids
    const selectedDisciplines = selectedDataFieldListItems.map(({ id }) => id);
    // Find the schedule version's discipline code
    const scheduleVersionDisciplineValue = eventBlockScheduleVersion.item.dataFieldValues.find((dataFieldValue) => dataFieldValue.dataField.id === disciplineDataField.id);
    // Does selected discipline Ids contain the event blocks schedule discipline
    return selectedDisciplines.includes(scheduleVersionDisciplineValue.value.id);
};

/**
 Checks if the event block should be filtered out of the calendar depending on the filters
 If true the eventBlock should be shown
 If false the eventBlock should be filtered and not shown
*/
export const showEventBlockOnCalendar = (machineContext: ScheduleMachineContext, eventBlock: EventBlock) => {
    const { selectedVenueLocations, selectedScheduleVersionHierarchy, selectedDataFieldListItems, schedule, day, filterableDataFieldValues, selectedViewByOption } = machineContext;
    const { filterType } = selectedViewByOption;

    const { calendarStartPosition, calendarEndPosition } = getCalendarRangeBasedOnFilterType(
        filterType,
        machineContext.availableDays,
        schedule.dayStartAt,
        schedule.dayEndAt,
        day,
        schedule.timezone
    );

    // Only sessions need to be checked as every other event block is can not be created unless the relating session is on calendar
    if (eventBlock.type.calendarLevel !== 1) {
        return true;
    }

    // If there is a day filter active
    if (filterType === FilterType.DayDataField || filterType === FilterType.DayVenue) {
        const dayFilter = isEventBlockValidByDateFilter(calendarStartPosition, calendarEndPosition, eventBlock);
        if (!dayFilter) {
            return false;
        }
    }

    switch (filterType) {
        case FilterType.Venue:
        case FilterType.DayVenue:
        case FilterType.Summary:
            // Check venue filter
            return isVenueSelected(selectedVenueLocations, eventBlock);
        case FilterType.DataField:
        case FilterType.DayDataField:
            // Check discipline filter
            return isDisciplineSelected(selectedScheduleVersionHierarchy, selectedDataFieldListItems, filterableDataFieldValues, eventBlock);
        default:
            return true;
    }
};
